/* @flow */

import {
  constant,
  flow,
  get,
  keyBy,
  keys,
  mapValues,
  merge,
  noop,
  omit,
  omitBy,
  reduce,
  set,
  toString,
  unset,
  without
} from 'lodash/fp';

import createReducer from '../utils/createReducer';
import {
  ADD_BONUS,
  ADD_ENTITY,
  API_CALL_SUCCESS,
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  LOCK_ALL,
  LOCK_ENTITY,
  REMOVE_BONUS,
  REMOVE_ENTITY,
  RESET_CHALLENGE,
  RESET_LINEUP_ERRORS,
  RESET_LINEUP,
  SELECT_BONUS,
  SET_LINE_UP,
  SET_GAME_FILTER,
  SET_POSITION_FILTER,
  SMART_LINEUP_FAILED,
  SUBMIT_CHALLENGE_FAILURE,
  SUBMIT_CHALLENGE_REQUEST,
  SUBMIT_CHALLENGE_SUCCESS,
  SWITCH_SELECTED_POSITION,
  UNLOCK_ALL,
  UNLOCK_ENTITY,
  UPDATE_CHALLENGE_FAILURE,
  UPDATE_CHALLENGE_REQUEST,
  UPDATE_CHALLENGE_SUCCESS,
  TOGGLE_POSITION_FILTER
} from '../constants/actionTypes';
import {
  CREATE_CHALLENGE,
  USER_SIGN_OUT,
  CHALLENGE,
  H2H_CHALLENGE,
  UPDATE_MY_LINEUP
} from '../constants/apiTypes';
import { mapErrorMessages } from '../utils/challengeUtils';
import switchFunc from '../utils/switchFunc';

const mapBonusesFromAPI = flow(keyBy('key'), mapValues('player_id'));

const mapPlayersFromAPI = reduce((memo, player) => {
  const pos = get('position.key', player);
  const idStr = `PLAYER_${player.id}`;

  return {
    ...memo,
    [pos]: {
      ...memo[pos],
      [idStr]: { locked: true, isTeam: false }
    }
  };
}, {});

const lockAll = mapValues(mapValues(set('locked', true)));
const unlockAll = mapValues(mapValues(set('locked', false)));

export type LineUpState = {
  activeBonus: ?string,
  bonusesChosen: {
    [string]: string
  },
  challengeId: ?string,
  didAutofill: boolean,
  didLoadLineUp: boolean,
  didMakeSelection: boolean,
  errorMessages: Array<string>,
  gameFilter: ?string,
  isSubmitting: boolean,
  isUpdating: boolean,
  positionFilter: ?string,
  positionFilters: ?Array<string>,
  showAllPositionsFilter: boolean,
  smartLineUpFailed?: boolean,
  smartProjections?: number,
  selections: {
    [string]: {
      [string]: {
        locked: boolean
      }
    }
  }
};

const initialState: LineUpState = {
  activeBonus: null,
  bonusesChosen: {},
  challengeId: null,
  challengeIds: [],
  didAutofill: false,
  didLoadLineUp: false,
  didMakeSelection: false,
  errorMessages: [],
  favorites: [],
  gameFilter: null,
  isSubmitting: false,
  isUpdating: false,
  lineupUpdated: false,
  positionFilter: null,
  showAllPositionsFilter: true,
  smartLineUpFailed: false,
  smartProjections: null,
  positionFilters: [],
  selections: {},
  numChallengesEntered: 0
};

export function challengeReducer(state, data, overwrite = false) {
  const entry = get('current_user_entry', data);
  const bonuses = get('selection.bonus_selections', entry) || [];
  const playerSelections = mapPlayersFromAPI(get('selection.players', entry) || []);
  const teamPosition =
    get('rule_set.selection_rules.team_selections', data) ||
    (get('rule_set.selection_rules.position_rules', data) || []).find(r => r.include_teams);
  const teamPositionKey = get('key', teamPosition) || get('short_label', teamPosition);

  const teamSelections = teamPositionKey
    ? {
        [teamPositionKey.toLowerCase()]: mapValues(
          constant({ locked: true, isTeam: true }),
          keyBy(({ id }) => `TEAM_${id}`, get('selection.teams', entry))
        )
      }
    : {};

  return overwrite
    ? {
        bonusesChosen: mapBonusesFromAPI(bonuses),
        selections: merge(playerSelections, teamSelections)
      }
    : undefined;
}

const apiCallRequestReducer = switchFunc(
  {
    [CREATE_CHALLENGE]: () => ({
      isSubmitting: true,
      numChallengesEntered: 0,
      errorMessages: []
    }),
    [UPDATE_MY_LINEUP]: () => ({
      isSubmitting: true,
      lineupUpdated: false,
      errorMessages: []
    }),
    [H2H_CHALLENGE]: () => ({
      errorMessages: []
    })
  },
  () => noop
);

const apiCallSuccessReducer = switchFunc(
  {
    [USER_SIGN_OUT]: state => ({
      ...initialState,
      challengeId: state.challengeId
    }),
    [CHALLENGE]: (state, { data, force }) => {
      const availablePositions = get('rule_set.selection_rules.position_rules', data) || [];

      const showAllPositionsFilter = !data.is_captain_contest && availablePositions.length > 1;

      const defaultPositionFilter = showAllPositionsFilter
        ? null // null is for all
        : get('[0].key', availablePositions);

      const challengeId = toString(data.id);

      const overwrite =
        (force && data.current_user_entry) ||
        state.challengeId === null ||
        challengeId !== state.challengeId;
      return {
        ...state,
        ...challengeReducer(state, data, overwrite),
        challengeId,
        didAutofill: !overwrite && state.didAutofill,
        didLoadLineUp: !overwrite && state.didLoadLineUp,
        didMakeSelection: !overwrite && state.didMakeSelection,
        positionFilter:
          challengeId === state.challengeId ? state.positionFilter : defaultPositionFilter,
        showAllPositionsFilter
      };
    },
    [CREATE_CHALLENGE]: (state, action) => ({
      isSubmitting: false,
      numChallengesEntered: action.data.length,
      errorMessages: mapErrorMessages(action.meta.soft_errors),
      challengeIds: action.data.map(c => c.id)
    }),
    [UPDATE_MY_LINEUP]: (state, action) => ({
      isSubmitting: false,
      lineupUpdated: true,
      errorMessages: mapErrorMessages(action.meta.soft_errors)
    })
  },
  () => noop
);

const apiCallFailureReducer = switchFunc(
  {
    [CREATE_CHALLENGE]: (state, action) => ({
      errorMessages: mapErrorMessages(action.errors),
      isSubmitting: false
    }),
    [UPDATE_MY_LINEUP]: (state, action) => ({
      errorMessages: mapErrorMessages(action.errors),
      isSubmitting: false
    })
  },
  () => noop
);

export function normaliseSelections(state, action) {
  return reduce(
    (memo, pos) => ({
      ...memo,
      [pos]: reduce(
        (m, { idStr, isTeam }) => ({
          ...m,
          [idStr]: {
            locked: action.lock || get([pos, idStr, 'locked'], state.selections),
            isTeam
          }
        }),
        {},
        action.selections[pos]
      )
    }),
    {},
    keys(action.selections)
  );
}

export function getBonusChosen(state, action) {
  return action.bonuses ? action.bonuses : state.bonusesChosen;
}

const lineUpReducer = createReducer(initialState, {
  [ADD_BONUS]: (state, { key, playerId }) => ({
    activeBonus: null,
    bonusesChosen: set(key, playerId, state.bonusesChosen)
  }),

  [API_CALL_SUCCESS]: (state, action) => apiCallSuccessReducer(action.api)(state, action),
  [API_CALL_REQUEST]: (state, action) => apiCallRequestReducer(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCallFailureReducer(action.api)(state, action),

  [ADD_ENTITY]: (state, { entity, position }) => ({
    didMakeSelection: true,
    selections: {
      ...state.selections,
      [position]: set(
        entity.idStr,
        { locked: true, isTeam: entity.isTeam },
        state.selections[position]
      )
    }
  }),

  [LOCK_ENTITY]: (state, { entity, position }) => ({
    selections: set([position, entity, 'locked'], true, state.selections)
  }),

  [LOCK_ALL]: state => ({
    selections: lockAll(state.selections)
  }),

  [UNLOCK_ALL]: state => ({
    selections: unlockAll(state.selections)
  }),

  [REMOVE_BONUS]: (state, { key }) => ({
    bonusesChosen: omit(key, state.bonusesChosen)
  }),

  [REMOVE_ENTITY]: (state, { entity, position, switchPosition }) => ({
    positionFilter: switchPosition ? position : state.positionFilter,
    selections: unset([position, entity], state.selections),
    bonusesChosen: omitBy(value => toString(value) === entity)(state.bonusesChosen)
  }),

  [SWITCH_SELECTED_POSITION]: (state, { entity, position, newPosition }) => {
    const selection = get(entity, state.selections[position]);
    const selectionsData = unset([position, entity], state.selections);
    return {
      didMakeSelection: true,
      selections: {
        ...selectionsData,
        [newPosition]: set(entity, selection, selectionsData[newPosition])
      }
    };
  },

  [RESET_LINEUP]: () => ({
    activeBonus: null,
    bonusesChosen: {},
    didAutofill: false,
    didLoadLineUp: false,
    didMakeSelection: false,
    smartLineUpFailed: false,
    selections: {}
  }),

  [RESET_LINEUP_ERRORS]: () => ({
    errorMessages: []
  }),

  [SMART_LINEUP_FAILED]: () => ({
    smartLineUpFailed: true
  }),

  [RESET_CHALLENGE]: () => initialState,

  [SELECT_BONUS]: (state, { key }) => ({
    activeBonus: key
  }),

  [SET_LINE_UP]: (state, action) => ({
    didAutofill: !!action.autofill,
    didLoadLineUp: !!action.loadLineUp,
    bonusesChosen: getBonusChosen(state, action),
    selections: normaliseSelections(state, action),
    smartLineUpFailed: !!action.smartLineUpFailed,
    smartProjections: action.smartProjections
  }),

  [SET_GAME_FILTER]: (state, { game }) => ({
    gameFilter: game
  }),

  [SET_POSITION_FILTER]: (state, { position }) => ({
    positionFilter: position
  }),

  [TOGGLE_POSITION_FILTER]: (state, { position, exclusive }) => {
    if (!position) {
      return {
        positionFilters: []
      };
    }

    if (exclusive) {
      return {
        positionFilters: [position],
        positionFilter: position
      };
    }

    return {
      positionFilters: state.positionFilters.includes(position)
        ? without([position], state.positionFilters)
        : [...state.positionFilters, position]
    };
  },

  [SUBMIT_CHALLENGE_REQUEST]: () => ({
    errorMessages: [],
    isSubmitting: true
  }),

  [SUBMIT_CHALLENGE_SUCCESS]: () => ({
    isSubmitting: false
  }),

  [SUBMIT_CHALLENGE_FAILURE]: (state, { payload }) => ({
    errorMessages: mapErrorMessages(payload.errors),
    isSubmitting: false
  }),

  [UNLOCK_ENTITY]: (state, { entity, position }) => ({
    selections: set([position, entity, 'locked'], false, state.selections)
  }),

  [UPDATE_CHALLENGE_REQUEST]: () => ({
    errorMessages: [],
    isUpdating: true
  }),

  [UPDATE_CHALLENGE_SUCCESS]: () => ({
    isUpdating: false
  }),

  [UPDATE_CHALLENGE_FAILURE]: (state, { payload }) => ({
    errorMessages: mapErrorMessages(payload.errors),
    isUpdating: false
  })
});

export default lineUpReducer;
